import create from 'zustand';
import { doc, Timestamp } from 'firebase/firestore';
import { MachineSession } from '../datamodels';
import { firestore } from '../firebase';

type MachineSessionUpdater =
  | MachineSession[]
  | ((prev: MachineSession[]) => MachineSession[]);

export type SyncStatus = 'offline' | 'local' | 'syncing' | 'synced';

export interface TrainingState {
  sessionId: string | null;
  startedAt: number | null;
  machineSessions: MachineSession[];
  hasHydrated: boolean;
  syncStatus: SyncStatus;

  hydrateFromStorage: () => void;
  startLocalSession: (id: string, startedAt: number) => void;
  finishLocalSession: () => void;
  clearLocalSession: () => void;
  setMachineSessions: (updater: MachineSessionUpdater) => void;
  setSyncStatus: (status: SyncStatus) => void;
}

const serializeMachineSessions = (sessions: MachineSession[]) =>
  sessions.map(session => ({
    ...session,
    machine_ref: { id: session.machine_ref.id },
    dateUsedMs: session.date_used.toMillis(),
    date_used: undefined,
  }));

const storedDateToMillis = (session: any): number => {
  if (typeof session.dateUsedMs === 'number') return session.dateUsedMs;
  if (typeof session.date_used?.seconds === 'number') {
    return session.date_used.seconds * 1000;
  }
  if (typeof session.date_used?._seconds === 'number') {
    return session.date_used._seconds * 1000;
  }
  return Date.now();
};

const deserializeMachineSessions = (raw: string | null): MachineSession[] => {
  if (!raw) return [];

  try {
    const parsed: any[] = JSON.parse(raw);
    return parsed.map(session => ({
      ...session,
      machine_ref: doc(firestore, 'machines', session.machine_ref.id),
      date_used: Timestamp.fromMillis(storedDateToMillis(session)),
    }));
  } catch (error) {
    console.error('Failed to parse stored machineSessions:', error);
    return [];
  }
};

const readStartedAt = (): number | null => {
  const raw = localStorage.getItem('activeWorkout');
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    return typeof parsed.start === 'number' ? parsed.start : null;
  } catch {
    return null;
  }
};

export const useTrainingStore = create<TrainingState>((set, get) => ({
  sessionId: null,
  startedAt: null,
  machineSessions: [],
  hasHydrated: false,
  syncStatus: navigator.onLine ? 'synced' : 'offline',

  hydrateFromStorage: () => {
    if (get().hasHydrated) return;

    const sessionId = localStorage.getItem('sessionId');
    const startedAt = readStartedAt();
    const machineSessions = deserializeMachineSessions(
      localStorage.getItem('machineSessions')
    );

    set({
      sessionId,
      startedAt,
      machineSessions,
      hasHydrated: true,
      syncStatus: navigator.onLine ? 'synced' : 'offline',
    });
  },

  startLocalSession: (id, startedAt) => {
    localStorage.setItem('sessionId', id);
    localStorage.setItem('activeWorkout', JSON.stringify({ start: startedAt }));
    set({
      sessionId: id,
      startedAt,
      machineSessions: [],
      hasHydrated: true,
      syncStatus: navigator.onLine ? 'syncing' : 'local',
    });
  },

  finishLocalSession: () => {
    localStorage.removeItem('sessionId');
    localStorage.removeItem('activeWorkout');
    localStorage.removeItem('machineSessions');
    set({
      sessionId: null,
      startedAt: null,
      machineSessions: [],
      syncStatus: navigator.onLine ? 'synced' : 'offline',
    });
  },

  clearLocalSession: () => {
    localStorage.removeItem('sessionId');
    localStorage.removeItem('activeWorkout');
    localStorage.removeItem('machineSessions');
    set({
      sessionId: null,
      startedAt: null,
      machineSessions: [],
      syncStatus: navigator.onLine ? 'synced' : 'offline',
    });
  },

  setMachineSessions: updater => {
    set(state => {
      const machineSessions =
        typeof updater === 'function' ? updater(state.machineSessions) : updater;

      if (state.hasHydrated) {
        localStorage.setItem(
          'machineSessions',
          JSON.stringify(serializeMachineSessions(machineSessions))
        );
      }

      return {
        machineSessions,
        syncStatus: navigator.onLine ? 'syncing' : 'local',
      };
    });
  },

  setSyncStatus: syncStatus => set({ syncStatus }),
}));
